import _ from 'lodash';
import { call, put, takeLatest, delay } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { getCatalogFieldMappings, updateCatalogFieldMappings } from 'api/CatalogAPI';
import { getErrorResponse } from 'utils/xhr';
import { getNestedObject } from 'utils/helpers';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer';
import {
  fetchCatalogFieldTypesResponse,
  fetchCatalogFieldTypesError,
  updateCatalogFieldTypesError,
  updateAndFetchFieldTypesError,
} from './actions';
import { createFieldMappings } from './helpers';
import { SUCCESS_RESP, FIELDS_UPDATED_TIMEOUT_MESSAGE } from './constants';
import * as actionTypes from './actionTypes';

export function* fetchCatalogFieldTypesRequest({ tableName }) {
  try {
    const { data } = yield call(getCatalogFieldMappings, tableName);
    const definedFieldTypes = _.get(data, 'definedMappings');
    const undefinedFieldTypes = _.get(data, 'undefinedFields');

    // getCatalogFieldMappings returns an object that looks like
    // {
    //  definedMappings: { 'foo': 'string', 'bar': 'object', 'bar.baz': 'boolean' },
    //  undefinedFields: [ 'a', 'b', 'a.c', 'a.d', 'bar.foo', 'a.c' ],
    // }
    //
    // `definedMappings` will always be a flat object.
    // the children of fields that are of type 'object' will appear with dot notation
    // currently we do not allow for nested objects

    const all = {
      ..._.merge(
        getNestedObject(undefinedFieldTypes, 'undefined'),
        getNestedObject(definedFieldTypes),
      ),
    };

    const fieldTypes = {
      undefined: undefinedFieldTypes,
      all,
    };
    yield put(fetchCatalogFieldTypesResponse(tableName, fieldTypes));
  } catch (error) {
    yield put(fetchCatalogFieldTypesError(error.toString()));
  }
}

export function* updateCatalogFieldTypesRequest({ tableName, fieldTypes }) {
  try {
    const mappingsUpdates = createFieldMappings(fieldTypes);
    const catalogName = tableName;
    const { data } = yield call(updateCatalogFieldMappings, catalogName, mappingsUpdates);
    let successMsg = SUCCESS_RESP;
    if (mappingsUpdates.length > 1) {
      successMsg = `${successMsg}s`;
    }
    yield put(addPositiveToast(data || successMsg));
    return true;
  } catch (error) {
    const err = getErrorResponse(error);
    const errMsg = `Cannot update field types. ${_.get(err, ['error', 'reason'], err)}`;
    yield put(updateCatalogFieldTypesError(error.toString()));
    yield put(addNegativeToast(errMsg));
    return false;
  }
}

export function* updateAndFetchFieldTypes({ tableName, fieldTypes }) {
  try {
    const step1 = yield* updateCatalogFieldTypesRequest({ tableName, fieldTypes });
    if (step1) {
      yield* fetchCatalogFieldTypesRequest({ tableName });
    }
  } catch (error) {
    yield put(updateAndFetchFieldTypesError(error.toString()));
  }
}

export function* checkIfFieldTypesUpdated({ tableName, fields }) {
  try {
    // try up to 5 times
    for (let count = 0; count < 5; count += 1) {
      const { data } = yield call(getCatalogFieldMappings, tableName);
      const definedFieldTypes = _.get(data, 'definedMappings');
      const diff = _.difference(_.keys(fields), _.keys(definedFieldTypes));
      if (diff.length === 0) {
        return true;
      }
      yield* delay(1000);
    }
    throw Error('Field types not updated');
  } catch (error) {
    yield put(addNegativeToast(FIELDS_UPDATED_TIMEOUT_MESSAGE));
    return false;
  }
}

addToRootSaga([
  takeLatest(actionTypes.FETCH_CATALOG_FIELD_TYPES_REQUEST, fetchCatalogFieldTypesRequest),
  takeLatest(actionTypes.UPDATE_CATALOG_FIELD_TYPES_REQUEST, updateCatalogFieldTypesRequest),
  takeLatest(actionTypes.UPDATE_AND_FETCH_CATALOG_FIELD_TYPES, updateAndFetchFieldTypes),
]);
